import React from "react";
import { ColumnDef } from "@tanstack/react-table";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import ReactJson from "react-json-view";
import service from "../../service";

function normalizeJsonSrc(input: string | undefined) {
  let parsed: any;
  try {
    parsed = input ? JSON.parse(input) : {};
  } catch {
    parsed = {};
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return { data: parsed };
  }
  return parsed;
}

const ResendCell: React.FC<{ id: string; typeLogs: string }> = ({ id, typeLogs }) => {
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = React.useState(false);

  if (typeLogs !== "transfer") {
    return <div className="text-center text-sm w-20">-</div>;
  }

  const handleResend = async () => {
    setIsLoading(true);
    try {
      await service.resendTransfer(id);
      await queryClient.invalidateQueries({ queryKey: ["logs"] });
      alert("Resend success");
    } catch (error: any) {
      alert(error?.message || "Resend fail");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center gap-2">
      <button
        className="px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm disabled:opacity-60 disabled:cursor-not-allowed flex items-center"
        onClick={handleResend}
        disabled={isLoading}
        aria-busy={isLoading}
      >
        {isLoading && (
          <span className="inline-block w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin" />
        )}
        {isLoading ? "Sending..." : "Resend"}
      </button>
    </div>
  );
};

export const COLUMNS: ColumnDef<any>[] = [
  {
    accessorKey: "createDate",
    header: "Date",
    cell: (data) => {
      const dateString = data.getValue() as string;
      const date = dayjs(dateString).format("DD-MM-YYYY HH:mm:ss");

      return <div className="text-center text-sm w-32">{date}</div>;
    },
  },
  {
    accessorKey: "httpHeaders",
    header: "httpHeaders",
    cell: (data) => {
      const JSONString = data.getValue<string>();
      const src = normalizeJsonSrc(JSONString);

      return (
        <div className="w-96">
          <div className="text-xs">
            <span className="font-bold">Method : </span>
            <span>
              {data.row.original.httpMethod
                .replace(/"/g, "")
                .toUpperCase()}
            </span>
          </div>
          <div className="text-xs">
            <span className="font-bold">URL : </span>
            <span className="break-words">{data.row.original.endPoint}</span>
          </div>
          <ReactJson
            name={false}
            displayDataTypes={false}
            collapsed={data.row.index === 0 ? 1 : true}
            src={src}
          />
        </div>
      );
    },
  },
  {
    accessorKey: "typeLogs",
    header: "Type",
    cell: (data) => {
      const type = data.getValue() as string;

      return (
        <div className="text-center text-sm w-20 font-bold">
          {type[0].toUpperCase()}
          {type.slice(1)}
        </div>
      );
    },
  },
  {
    accessorKey: "event",
    header: "Event",
    cell: (data) => {
      const event = data.getValue() as string;

      return (
        <div className="text-center text-sm font-bold">
          {event}
        </div>
      );
    },
  },
  {
    accessorKey: "errorCode",
    header: "Error Code",
    cell: (data) => {
      const errorCode = data.getValue() as string;

      return (
        <div className="text-sm variable-value">
          {errorCode}
        </div>
      );
    },
  },
  {
    accessorKey: "payload",
    header: "Payload",
    cell: (data) => {
      const JSONString = data.getValue<string>();
      const src = JSONString ? normalizeJsonSrc(JSONString) : undefined;

      return (
        <div className="w-80">
          {src ? (
            <ReactJson
              name={false}
              displayDataTypes={false}
              collapsed={data.row.index === 0 ? 1 : true}
              src={src}
            />
          ) : (
            <div className="text-center">-</div>
          )}
        </div>
      );
    },
  },
  {
    accessorKey: "respone",
    header: "Response",
    cell: (data) => {
      const JSONString = data.getValue<string>();
      const src = normalizeJsonSrc(JSONString);

      return (
        <div className="w-56">
          <div className="text-xs font-bold">
            Status : {data.row.original.httpStatus}
          </div>
          <ReactJson
            name={false}
            displayDataTypes={false}
            collapsed={data.row.index === 0 ? 1 : true}
            src={src}
          />
        </div>
      );
    },
  },
  {
    id: "resend",
    header: "Resend",
    cell: ({ row }) => (
      <ResendCell id={row.original._id} typeLogs={row.original.typeLogs} />
    ),
  },
];
